// src/pages/EventMap.js - single-event map (Apple MapKit JS)
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AppleMap from '../components/Map/AppleMap';
import { toCoordinate, markAnnotationTap } from '../services/appleMapKit';
import useSmartBack from '../hooks/useSmartBack';

const EventMap = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [showInfoWindow, setShowInfoWindow] = useState(true);
  const navigate = useNavigate();
  const handleBack = useSmartBack();

  const mapRef = useRef(null);
  const mapkitRef = useRef(null);
  const markerRef = useRef(null);
  const eventRef = useRef(null);
  const showInfoRef = useRef(true);

  eventRef.current = event;
  showInfoRef.current = showInfoWindow;

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await fetch(`/events/${id}`);
        const data = await response.json();
        setEvent(data);
        setMapCenter({ lat: parseFloat(data.latitude), lng: parseFloat(data.longitude) });
      } catch (error) {
        console.error('Error fetching event data:', error);
      }
    };

    fetchEvent();
  }, [id]);

  const buildCallout = useCallback(() => {
    const ev = eventRef.current;
    const div = document.createElement('div');
    div.style.cssText =
      'background:white;border-radius:8px;box-shadow:0 2px 10px rgba(0,0,0,0.25);padding:10px;max-width:220px;cursor:pointer;';

    const title = document.createElement('h3');
    title.style.cssText = 'margin:0 0 4px;font-size:14px;';
    title.textContent = (ev && ev.event_name) || '';
    div.appendChild(title);

    const loc = document.createElement('p');
    loc.style.cssText = 'margin:0 0 6px;font-size:12px;color:#555;';
    loc.textContent = (ev && ev.event_location) || '';
    div.appendChild(loc);

    if (ev && ev.event_image_link) {
      const img = document.createElement('img');
      img.src = ev.event_image_link;
      img.alt = ev.event_name || '';
      img.style.cssText = 'width:100px;height:auto;border-radius:4px;';
      div.appendChild(img);
    }

    div.addEventListener('click', () => {
      if (eventRef.current && eventRef.current.id) {
        navigate(`/event/${eventRef.current.id}`);
      }
    });
    return div;
  }, [navigate]);

  // Create the marker once map + event are both ready
  useEffect(() => {
    const map = mapRef.current;
    const mapkit = mapkitRef.current;
    if (!map || !mapkit || !mapCenter || markerRef.current) return;

    const marker = new mapkit.MarkerAnnotation(
      toCoordinate(mapCenter.lat, mapCenter.lng),
      {
        title: (event && event.event_name) || '',
        animates: false,
        callout: {
          calloutElementForAnnotation: () => buildCallout(),
        },
      }
    );
    marker.addEventListener('select', () => {
      markAnnotationTap(map);
      setShowInfoWindow(true);
    });
    map.addAnnotation(marker);
    if (showInfoRef.current) {
      // Open callout initially
      setTimeout(() => {
        try {
          map.selectedAnnotation = marker;
        } catch (e) {
          /* noop */
        }
      }, 300);
    }
    markerRef.current = marker;
  }, [mapCenter, event, buildCallout]);

  // Reflect showInfoWindow state on the callout
  useEffect(() => {
    const map = mapRef.current;
    const marker = markerRef.current;
    if (!map || !marker) return;
    try {
      map.selectedAnnotation = showInfoWindow ? marker : null;
    } catch (e) {
      /* noop */
    }
  }, [showInfoWindow]);

  const handleMapClick = () => {
    if (showInfoRef.current) setShowInfoWindow(false);
  };

  return (
    <div style={{ width: '100%', height: '100vh', position: 'relative' }}>
      <IconButton
        onClick={handleBack}
        aria-label="ย้อนกลับ"
        sx={{
          position: 'absolute',
          top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
          left: 12,
          zIndex: 1000,
          bgcolor: 'rgba(255,255,255,0.95)',
          boxShadow: 2,
          '&:hover': { bgcolor: '#fff' }
        }}
      >
        <ArrowBackIcon />
      </IconButton>
      <AppleMap
        center={mapCenter || { lat: 13.7563, lng: 100.5018 }}
        zoom={10}
        onClick={handleMapClick}
        onMapReady={(map, mapkit) => {
          mapRef.current = map;
          mapkitRef.current = mapkit;
        }}
      />
    </div>
  );
};

export default EventMap;
